// Translates everything on screen from src/i18n/catalog/<language>.json, so pages can keep
// plain English text. React still owns each node: only its text is changed, and when React
// writes new text it is translated again. Text missing from a catalog stays in English.
// Mark anything that must not change (names, codes) with translate="no".
import i18n from './index';
import { compileCatalog, translateText } from '../lib/i18n/translate';
import type { CompiledCatalog } from '../lib/i18n/translate';

const loaders: Record<string, () => Promise<{ default: Record<string, string> }>> = {
  zh: () => import('./catalog/zh.json'),
  km: () => import('./catalog/km.json'),
};
const ATTRIBUTES = ['placeholder', 'title', 'aria-label', 'alt'];
const SKIP = 'script,style,code,pre,textarea,[translate="no"],[contenteditable="true"]';

const compiled = new Map<string, CompiledCatalog>();
let active: CompiledCatalog | null = null;
const textRecords = new WeakMap<Text, { original: string; shown: string }>();
const attributeRecords = new WeakMap<Element, Map<string, { original: string; shown: string }>>();

function applyToText(node: Text) {
  if (node.parentElement?.closest(SKIP)) return;
  const current = node.nodeValue ?? '';
  const record = textRecords.get(node);
  const original = record && record.shown === current ? record.original : current;
  const shown = active ? translateText(original, active) ?? original : original;
  textRecords.set(node, { original, shown });
  if (shown !== current) node.nodeValue = shown;
}

function applyToAttribute(element: Element, name: string) {
  if (element.closest(SKIP)) return;
  const current = element.getAttribute(name);
  if (current === null) return;
  let records = attributeRecords.get(element);
  if (!records) { records = new Map(); attributeRecords.set(element, records); }
  const record = records.get(name);
  const original = record && record.shown === current ? record.original : current;
  const shown = active ? translateText(original, active) ?? original : original;
  records.set(name, { original, shown });
  if (shown !== current) element.setAttribute(name, shown);
}

function applyToTree(root: Node) {
  if (root.nodeType === Node.TEXT_NODE) { applyToText(root as Text); return; }
  if (root.nodeType !== Node.ELEMENT_NODE) return;
  const element = root as Element;
  ATTRIBUTES.forEach((name) => applyToAttribute(element, name));
  const walker = document.createTreeWalker(element, NodeFilter.SHOW_TEXT | NodeFilter.SHOW_ELEMENT);
  for (let node = walker.nextNode(); node; node = walker.nextNode()) {
    if (node.nodeType === Node.TEXT_NODE) applyToText(node as Text);
    else ATTRIBUTES.forEach((name) => applyToAttribute(node as Element, name));
  }
}

async function switchLanguage(language: string) {
  const loader = loaders[language];
  if (!loader) { active = null; applyToTree(document.body); return; }
  if (!compiled.has(language)) compiled.set(language, compileCatalog((await loader()).default));
  if (i18n.language !== language) return; // The person switched again while this loaded.
  active = compiled.get(language) ?? null;
  applyToTree(document.body);
}

// Observers run before the browser paints, so new text never flashes in English.
export async function startPageTranslator() {
  await switchLanguage(i18n.language).catch(() => undefined);
  new MutationObserver((mutations) => {
    for (const mutation of mutations) {
      if (mutation.type === 'characterData') applyToText(mutation.target as Text);
      else if (mutation.type === 'attributes') applyToAttribute(mutation.target as Element, mutation.attributeName ?? '');
      else mutation.addedNodes.forEach(applyToTree);
    }
  }).observe(document.body, { subtree: true, childList: true, characterData: true, attributes: true, attributeFilter: ATTRIBUTES });
  i18n.on('languageChanged', (language) => { void switchLanguage(language).catch(() => undefined); });
}
